import React, { useState } from "react";
import { supabase } from '../../supabase';
import PasswordInput from '../shared/PasswordInput';

function AuthModal({ onClose, onAuth, defaultEmail, setTab, setShowEventGoerSignup, defaultMode, defaultRole }) {
  const [mode, setMode] = useState(defaultMode || 'login');
  const [email, setEmail] = useState(defaultEmail || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);
  const [roles, setRoles] = useState({ vendor: defaultRole==='vendor', host: defaultRole==='host', eventGoer: defaultRole==='eventGoer' });

  const toggleRole = (role) => setRoles(r => ({ ...r, [role]: !r[role] }));

  const handleSubmit = async () => {
    if (!email || !password) { setError('Please enter email and password.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (mode === 'signup' && !roles.vendor && !roles.host && !roles.eventGoer) { setError('Please select at least one role.'); return; }
    setLoading(true); setError('');
    if (mode === 'signup') {
      const { error: signUpErr } = await supabase.auth.signUp({ email, password });
      if (signUpErr) { setError(signUpErr.message); setLoading(false); return; }
      // Save roles for post-login routing
      localStorage.setItem('sjvm_pending_roles', JSON.stringify(roles));
    } else {
      const { error: signInErr } = await supabase.auth.signInWithPassword({ email, password });
      if (signInErr) { setError(signInErr.message); setLoading(false); return; }
    }
    setLoading(false);
    if (onAuth) onAuth();
    onClose();
    // Route based on roles after signup
    if (mode === 'signup' && setTab) {
      if (roles.vendor) setTab('vendor');
      else if (roles.host) setTab('host');
      else if (roles.eventGoer && setShowEventGoerSignup) setShowEventGoerSignup(true);
    }
  };

  const handleReset = async () => {
    if (!email) { setError('Enter your email first.'); return; }
    const { error: resetErr } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: 'https://sj-vendor-match.vercel.app',
    });
    if (resetErr) { setError(resetErr.message); return; }
    setResetSent(true);
  };

  const handleSetNewPassword = async () => {
    if (!password) { setError('Please enter a new password.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }
    setLoading(true); setError('');
    const { error: updateErr } = await supabase.auth.updateUser({ password });
    if (updateErr) { setError(updateErr.message); setLoading(false); return; }
    setLoading(false);
    setResetComplete(true);
  };

  const roleBtn = (role, label, emoji, desc) => (
    <button onClick={()=>toggleRole(role)} style={{
      flex:1, minWidth:140, padding:'14px 12px', borderRadius:10, cursor:'pointer',
      border: roles[role] ? '2px solid #c8a84b' : '2px solid #e8ddd0',
      background: roles[role] ? '#fdf9f0' : '#fff',
      fontFamily:'DM Sans,sans-serif', textAlign:'center', transition:'all 0.15s',
    }}>
      <div style={{fontSize:22,marginBottom:4}}>{emoji}</div>
      <div style={{fontSize:14,fontWeight:700,color:'#1a1410'}}>{label}</div>
      <div style={{fontSize:11,color:'#7a6a5a',marginTop:2}}>{desc}</div>
      {roles[role] && <div style={{color:'#c8a84b',fontSize:16,marginTop:4}}>&#10003;</div>}
    </button>
  );

  return (
    <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:16,maxWidth:440,width:'100%',overflow:'hidden'}}>
        <div style={{background:'#1a1410',padding:'24px 28px',textAlign:'center'}}>
          <div style={{fontSize:20,fontWeight:700,color:'#e8c97a',fontFamily:'Playfair Display,serif'}}>
            {mode==='resetPassword' ? 'Set New Password' : mode==='login' ? 'Welcome Back' : 'Welcome to South Jersey Vendor Market!'}
          </div>
          <div style={{fontSize:13,color:'#a89a8a',marginTop:4}}>{mode==='resetPassword' ? 'Choose a new password for your account' : mode==='login' ? 'South Jersey Vendor Market' : 'Create your free account \u2014 select all that apply'}</div>
        </div>
        <div style={{padding:'24px 28px'}}>
          {mode === 'resetPassword' ? (
            resetComplete ? (
              <div style={{textAlign:'center',padding:'16px 0'}}>
                <div style={{fontSize:28,marginBottom:8}}>{'\u2705'}</div>
                <div style={{fontSize:15,fontWeight:600,color:'#1a6b3a',marginBottom:8}}>Password updated!</div>
                <div style={{fontSize:13,color:'#7a6a5a'}}>Your password has been changed. You are now logged in.</div>
                <button onClick={onClose} style={{marginTop:16,background:'#1a1410',color:'#e8c97a',border:'none',borderRadius:8,padding:'10px 24px',fontSize:14,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>Continue</button>
              </div>
            ) : (
              <>
                <div className="form-group" style={{marginBottom:12}}>
                  <label>New Password</label>
                  <PasswordInput value={password} onChange={e=>setPassword(e.target.value)}
                    placeholder="New password (min 6 chars)"
                    onKeyDown={e=>{if(e.key==='Enter')handleSetNewPassword();}} />
                </div>
                <div className="form-group" style={{marginBottom:16}}>
                  <label>Confirm Password</label>
                  <PasswordInput value={confirmPassword} onChange={e=>setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    onKeyDown={e=>{if(e.key==='Enter')handleSetNewPassword();}} />
                </div>
                {error && <div style={{color:'#c0392b',fontSize:13,marginBottom:12,background:'#fdecea',border:'1px solid #f5c6c6',borderRadius:8,padding:'8px 12px'}}>{error}</div>}
                <button onClick={handleSetNewPassword} disabled={loading}
                  style={{width:'100%',background:'#c8a84b',color:'#1a1410',border:'none',borderRadius:8,padding:'12px 0',fontSize:15,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif',opacity:loading?0.5:1}}>
                  {loading ? 'Updating...' : 'Set New Password'}
                </button>
              </>
            )
          ) : resetSent ? (
            <div style={{textAlign:'center',padding:'16px 0'}}>
              <div style={{fontSize:28,marginBottom:8}}>{'\ud83d\udce7'}</div>
              <div style={{fontSize:15,fontWeight:600,color:'#1a6b3a',marginBottom:8}}>Check your email</div>
              <div style={{fontSize:13,color:'#7a6a5a'}}>We sent a password reset link to {email}</div>
              <div style={{fontSize:12,color:'#a89a8a',marginTop:6}}>Check your spam folder if you don't see it. Link expires in 24 hours.</div>
              <button onClick={onClose} style={{marginTop:16,background:'#1a1410',color:'#e8c97a',border:'none',borderRadius:8,padding:'10px 24px',fontSize:14,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>Close</button>
            </div>
          ) : (
            <>
              {mode === 'signup' && (
                <div style={{marginBottom:16}}>
                  <label style={{fontSize:13,fontWeight:600,color:'#1a1410',marginBottom:8,display:'block'}}>I am a... <span style={{fontWeight:400,color:'#a89a8a'}}>(select all that apply)</span></label>
                  <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                    {roleBtn('vendor', 'Vendor', '\ud83d\udecd\ufe0f', 'Sell products or services at events')}
                    {roleBtn('host', 'Event Host', '\ud83c\udfaa', 'Organize events and find vendors')}
                    {roleBtn('eventGoer', 'Event Guest', '\ud83d\udccd', 'Discover local markets near me')}
                  </div>
                  {(roles.vendor && roles.host) || (roles.vendor && roles.eventGoer) || (roles.host && roles.eventGoer) ? (
                    <div style={{background:'#e8f4fd',border:'1px solid #b8d8f0',borderRadius:8,padding:'8px 12px',marginTop:10,fontSize:12,color:'#1a4a6b'}}>
                      You'll have access to all selected features under one account.
                    </div>
                  ) : null}
                  <div style={{fontSize:11,color:'#a89a8a',marginTop:8,textAlign:'center'}}>You can always add more roles later from your dashboard.</div>
                </div>
              )}
              <div className="form-group" style={{marginBottom:12}}>
                <label>Email</label>
                <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@email.com"
                  onKeyDown={e=>{if(e.key==='Enter')handleSubmit();}} />
              </div>
              <div className="form-group" style={{marginBottom:16}}>
                <label>Password</label>
                <PasswordInput value={password} onChange={e=>setPassword(e.target.value)}
                  placeholder={mode==='signup'?'Create a password (min 6 chars)':'Your password'}
                  onKeyDown={e=>{if(e.key==='Enter')handleSubmit();}} />
              </div>
              {error && <div style={{color:'#c0392b',fontSize:13,marginBottom:12,background:'#fdecea',border:'1px solid #f5c6c6',borderRadius:8,padding:'8px 12px'}}>{error}</div>}
              <button onClick={handleSubmit} disabled={loading}
                style={{width:'100%',background:'#c8a84b',color:'#1a1410',border:'none',borderRadius:8,padding:'12px 0',fontSize:15,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif',marginBottom:12,opacity:loading?0.5:1}}>
                {loading ? 'Please wait...' : mode==='login' ? 'Log In' : 'Sign Up'}
              </button>
              <div style={{textAlign:'center',fontSize:13,color:'#7a6a5a'}}>
                {mode==='login' ? (
                  <>
                    <button onClick={handleReset} style={{background:'none',border:'none',color:'#c8a84b',cursor:'pointer',fontSize:13,fontFamily:'DM Sans,sans-serif',textDecoration:'underline',padding:0}}>Forgot password?</button>
                    <span style={{margin:'0 8px'}}>{'\u00b7'}</span>
                    <button onClick={()=>{setMode('signup');setError('');}} style={{background:'none',border:'none',color:'#c8a84b',cursor:'pointer',fontSize:13,fontFamily:'DM Sans,sans-serif',textDecoration:'underline',padding:0}}>Create account</button>
                  </>
                ) : (
                  <>Already have an account? <button onClick={()=>{setMode('login');setError('');}} style={{background:'none',border:'none',color:'#c8a84b',cursor:'pointer',fontSize:13,fontFamily:'DM Sans,sans-serif',textDecoration:'underline',padding:0}}>Log in</button></>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AuthModal;
